import type { PromptCardSendType } from './types';

/**
 * Backtick-wrapped placeholders in prompt cards (e.g. `document`, `person`) are meant to be replaced by the user.
 * When comparing sent text to the card prompt, we treat these as variable and only require fixed segments to match.
 */
const BACKTICK_PLACEHOLDER_REGEX = /`[^`]*`/g;

/**
 * Single source of truth: classifies how the user sent a prompt that came from a card. Returns null if the sent
 * text is not related to the card (e.g. user cleared and typed something else).
 * - Exact match → 'asIs'
 * - Card has placeholders, fixed segments match in order → 'placeholdersFilled'
 * - Related but edited ie user added or removed text → 'tweaked'
 * - User clicks prompt, deletes it, and types a prompt → null
 */
export const getPromptCardSendType = (
	normalizedSentText: string,
	normalizedOriginalPrompt: string,
): PromptCardSendType | null => {
	if (normalizedSentText === normalizedOriginalPrompt) {
		return 'asIs';
	}
	if (normalizedOriginalPrompt.length === 0) {
		return null;
	}
	const fixedSegments = normalizedOriginalPrompt.split(BACKTICK_PLACEHOLDER_REGEX);
	const hasPlaceholders = fixedSegments.length > 1;
	if (hasPlaceholders) {
		let searchFrom = 0;
		let firstSegmentMatchedAtStart = true;
		for (const segment of fixedSegments) {
			const trimmed = segment.trim();
			if (trimmed.length === 0) {
				continue;
			}
			const idx = normalizedSentText.indexOf(trimmed, searchFrom);
			if (idx === -1) {
				// User truncated or edited after matching some of the card — if we matched at least one
				// fixed segment, they started from the card so treat as tweaked
				if (searchFrom > 0) {
					return 'tweaked';
				}
				// No segment matched yet; check containment (strip placeholders so "on `x`" vs "on x" match)
				const originalWithoutPlaceholders = normalizedOriginalPrompt
					.replace(BACKTICK_PLACEHOLDER_REGEX, ' ')
					.replace(/\s+/g, ' ')
					.trim();
				if (
					normalizedOriginalPrompt.includes(normalizedSentText) ||
					normalizedSentText.includes(normalizedOriginalPrompt) ||
					originalWithoutPlaceholders.includes(normalizedSentText) ||
					normalizedSentText.includes(originalWithoutPlaceholders) ||
					originalWithoutPlaceholders.startsWith(normalizedSentText) ||
					normalizedSentText.startsWith(originalWithoutPlaceholders)
				) {
					return 'tweaked';
				}
				return null;
			}
			if (searchFrom === 0 && idx !== 0) {
				firstSegmentMatchedAtStart = false;
			}
			searchFrom = idx + trimmed.length;
		}
		// Trailing content after the last fixed segment
		if (normalizedSentText.length > searchFrom) {
			return 'tweaked';
		}
		return firstSegmentMatchedAtStart ? 'placeholdersFilled' : 'tweaked';
	}
	if (
		normalizedSentText.includes(normalizedOriginalPrompt) ||
		normalizedOriginalPrompt.includes(normalizedSentText)
	) {
		return 'tweaked';
	}
	return null;
};
